<template>
  <section ref="rootEl" class="mascot-intro" :class="{ visible: isVisible }">
    <!-- Warm entry wave — bridges the cream credibility strip to mocha -->
    <svg
      class="intro-wave-top"
      viewBox="0 0 1440 60"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path fill="#fdf8f3" d="M0,30 C360,60 1080,0 1440,30 L1440,0 L0,0 Z" />
    </svg>

    <!-- Soft cinematic gradient bg -->
    <div class="intro-bg-1"></div>
    <div class="intro-bg-2"></div>

    <!-- Floating ambient sparkles -->
    <span class="amb amb-1">✦</span>
    <span class="amb amb-2">✧</span>
    <span class="amb amb-3">✦</span>
    <span class="amb amb-4">✧</span>
    <span class="amb amb-5">✦</span>

    <div class="intro-inner">
      <div class="intro-eyebrow">MEET TILLY</div>

      <div class="intro-mascot">
        <TillyMascot variant="celebrate" cursor-track />
      </div>

      <h2 class="intro-quote">A concierge for experiences</h2>
      <p class="intro-sub">
        Tilly learns your taste, finds what's worth doing around you, and turns
        ideas into actual plans. She coordinates the group, makes the
        reservation, and even picks up the phone when needed. Scroll to see how
        she works.
      </p>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import TillyMascot from './TillyMascot.vue';

export default {
  name: 'MascotIntro',
  components: { TillyMascot },
  setup() {
    const rootEl = ref(null);
    const isVisible = ref(false);
    let observer = null;

    onMounted(() => {
      if (!('IntersectionObserver' in window) || !rootEl.value) {
        isVisible.value = true;
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              isVisible.value = true;
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.25 },
      );
      observer.observe(rootEl.value);
    });

    onBeforeUnmount(() => {
      if (observer) observer.disconnect();
    });

    return { rootEl, isVisible };
  },
};
</script>

<style scoped>
.mascot-intro {
  position: relative;
  width: 100%;
  padding: 9rem 1.5rem 7.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  /* Sharp dark mocha — the cream wave sits crisply on top, no fade. */
  background: linear-gradient(
    180deg,
    #17100a 0%,
    #1f1508 40%,
    #241a0c 70%,
    #17100a 100%
  );
  color: white;
  overflow: hidden;
}

/* Top wave bridge */
.intro-wave-top {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 60px;
  display: block;
  z-index: 1;
  pointer-events: none;
}

.intro-bg-1,
.intro-bg-2 {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
}
/* Desktop: blur filter for hazy glow. Mobile: no filter (120px blur
   buffers × 2 is a major contributor to iOS GPU OOM) — the multi-stop
   radial gradient override below produces the same halo visually. */
@media (min-width: 768px) {
  .intro-bg-1,
  .intro-bg-2 {
    filter: blur(120px);
  }
}
.intro-bg-1 {
  width: 60vw;
  height: 60vw;
  max-width: 700px;
  max-height: 700px;
  background: radial-gradient(
    circle,
    rgba(253, 173, 0, 0.22) 0%,
    transparent 70%
  );
  top: -10%;
  left: -10%;
  animation: drift 24s ease-in-out infinite;
}
.intro-bg-2 {
  width: 55vw;
  height: 55vw;
  max-width: 650px;
  max-height: 650px;
  background: radial-gradient(
    circle,
    rgba(244, 132, 108, 0.18) 0%,
    transparent 70%
  );
  bottom: -15%;
  right: -10%;
  animation: drift 30s ease-in-out infinite reverse;
}

@keyframes drift {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(30px, -40px);
  }
}

.amb {
  position: absolute;
  color: rgba(253, 173, 0, 0.45);
  text-shadow: 0 0 14px rgba(253, 173, 0, 0.3);
  font-size: 1.2rem;
  z-index: 1;
  pointer-events: none;
  animation: amb-twinkle 4s ease-in-out infinite;
}
.amb-1 {
  top: 18%;
  left: 12%;
  animation-delay: 0s;
  font-size: 1.4rem;
}
.amb-2 {
  top: 28%;
  right: 15%;
  animation-delay: 1s;
  color: rgba(244, 132, 108, 0.55);
}
.amb-3 {
  bottom: 30%;
  left: 18%;
  animation-delay: 2s;
  color: rgba(253, 210, 100, 0.4);
}
.amb-4 {
  bottom: 22%;
  right: 22%;
  animation-delay: 1.5s;
  font-size: 0.9rem;
  color: rgba(253, 173, 0, 0.5);
}
.amb-5 {
  top: 50%;
  left: 5%;
  animation-delay: 0.5s;
  font-size: 1rem;
}

@keyframes amb-twinkle {
  0%,
  100% {
    opacity: 0.2;
    transform: scale(0.7) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1.3) rotate(180deg);
  }
}

.intro-inner {
  position: relative;
  z-index: 2;
  max-width: 800px;
  width: 100%;
}

.intro-eyebrow {
  font-family: 'AirbnbCereal_W_Md', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: rgba(253, 173, 0, 0.9);
  margin-bottom: 2rem;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 1s ease 0.2s, transform 1s ease 0.2s;
}

.intro-mascot {
  display: flex;
  justify-content: center;
  margin-bottom: 2.5rem;
  opacity: 0;
  transform: scale(0.8) translateY(20px);
  transition: opacity 1.2s ease 0.4s,
    transform 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s;
}
.intro-mascot :deep(.mascot-wrap) {
  max-width: 220px;
}

.intro-quote {
  font-family: 'AirbnbCereal_W_Bd', -apple-system, sans-serif;
  font-size: clamp(2rem, 6.5vw, 4rem);
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.15;
  margin: 0 0 1.25rem;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 1.2s ease 0.7s,
    transform 1.2s cubic-bezier(0.22, 0.61, 0.36, 1) 0.7s;
}

.grad {
  background: linear-gradient(135deg, #fdad00 0%, #f4846c 50%, #58a8e1 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-style: italic;
}

.intro-sub {
  font-family: 'AirbnbCereal_W_Bk', -apple-system, sans-serif;
  font-size: clamp(1.05rem, 1.8vw, 1.35rem);
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.78);
  margin: 0 0 2rem;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 1s ease 0.9s, transform 1s ease 0.9s;
}

.intro-attrib {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.55);
  font-style: italic;
  letter-spacing: 0.02em;
  opacity: 0;
  transition: opacity 1s ease 1.1s;
}

.visible .intro-eyebrow,
.visible .intro-mascot,
.visible .intro-quote,
.visible .intro-sub,
.visible .intro-attrib {
  opacity: 1;
  transform: translateY(0) scale(1);
}

@media (max-width: 767px) {
  .mascot-intro {
    padding: 7rem 1.5rem 6rem;
    min-height: auto;
  }
  .intro-mascot :deep(.mascot-wrap) {
    max-width: 170px;
  }
  /* True gaussian blur via pre-rasterized SVG mask, tinted per orb
     with background-color. Matches desktop filter: blur(120px)
     visually without the per-frame GPU blur pass. */
  .intro-bg-1,
  .intro-bg-2 {
    mask-image: url('~@/assets/ambient/blur-mask.svg');
    -webkit-mask-image: url('~@/assets/ambient/blur-mask.svg');
    mask-size: 100% 100%;
    -webkit-mask-size: 100% 100%;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-position: center;
  }
  .intro-bg-1 {
    background: rgba(253, 173, 0, 0.22);
  }
  .intro-bg-2 {
    background: rgba(244, 132, 108, 0.18);
  }
}
</style>
